import React, { useEffect, useRef, useState } from "react";
import { Link, useSearchParams, useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { CheckCircle2, Loader2, XCircle, ArrowRight } from "lucide-react";

export default function FlwSuccessPage() {
  const [params] = useSearchParams();
  const txRef = params.get("tx_ref");
  const flwStatus = (params.get("status") || "").toLowerCase();
  const [state, setState] = useState({ status: "checking", details: null });
  const attempts = useRef(0);
  const timer = useRef(null);
  const navigate = useNavigate();

  useEffect(() => {
    if (!txRef) { setState({ status: "error", details: "Missing transaction reference" }); return; }
    if (flwStatus === "cancelled") { setState({ status: "expired", details: "You cancelled the payment." }); return; }

    const poll = async () => {
      attempts.current += 1;
      try {
        const { data } = await api.get(`/payments/flutterwave/verify/${encodeURIComponent(txRef)}`);
        if (data.payment_status === "paid") { setState({ status: "paid", details: data }); return; }
        if (data.status === "expired")      { setState({ status: "expired", details: data }); return; }
        if (attempts.current >= 8)          { setState({ status: "pending", details: data }); return; }
        timer.current = setTimeout(poll, 2500);
      } catch {
        if (attempts.current >= 3) { setState({ status: "error", details: "Could not verify payment. Please contact support." }); return; }
        timer.current = setTimeout(poll, 2500);
      }
    };
    poll();
    return () => timer.current && clearTimeout(timer.current);
  }, [txRef, flwStatus]);

  return (
    <div className="max-w-2xl mx-auto px-4 md:px-8 py-24 text-center" data-testid="flw-success-page">
      {state.status === "checking" && (
        <>
          <Loader2 size={40} className="animate-spin mx-auto mv-green-text" />
          <h1 className="font-display text-3xl mv-green-text mt-6">Confirming your payment…</h1>
          <p className="text-[14.5px] mt-3" style={{ color: "var(--mv-muted)" }}>Flutterwave usually confirms within a few seconds.</p>
        </>
      )}
      {state.status === "paid" && (
        <>
          <div className="w-16 h-16 rounded-full mx-auto flex items-center justify-center" style={{ background: "rgba(37,211,102,0.14)" }}>
            <CheckCircle2 size={32} style={{ color: "#0A7A2C" }} />
          </div>
          <h1 className="font-display text-3xl md:text-4xl mv-green-text mt-6">Payment received.</h1>
          <p className="mt-3 text-[15px]" style={{ color: "var(--mv-muted)" }}>
            Thank you. Your payment of{" "}
            <b className="mv-green-text">
              {new Intl.NumberFormat("en-GB", { style: "currency", currency: (state.details?.currency || "usd").toUpperCase(), maximumFractionDigits: 0 })
                .format((state.details?.amount_total || 0) / 100)}
            </b>{" "}
            is now recorded on your Personal Log.
          </p>
          <p className="font-accent italic text-lg mt-6" style={{ color: "var(--mv-gold)" }}>MAVULA Investment — Open Doors.</p>
          <div className="mt-8 flex justify-center gap-3">
            <Link to="/dashboard" className="btn-gold" data-testid="flw-success-dashboard">View Personal Log <ArrowRight size={16}/></Link>
            <Link to="/" className="btn-ghost">Back Home</Link>
          </div>
        </>
      )}
      {(state.status === "pending" || state.status === "expired" || state.status === "error") && (
        <>
          <div className="w-16 h-16 rounded-full mx-auto flex items-center justify-center" style={{ background: "rgba(212,175,55,0.16)" }}>
            <XCircle size={32} style={{ color: "var(--mv-gold)" }} />
          </div>
          <h1 className="font-display text-3xl mv-green-text mt-6">
            {state.status === "pending" ? "Payment still processing" : state.status === "expired" ? "Payment not completed" : "We couldn't verify your payment"}
          </h1>
          <p className="mt-3 text-[14.5px]" style={{ color: "var(--mv-muted)" }}>
            {typeof state.details === "string" ? state.details : "Please check your email or SMS for confirmation, or contact support with reference: " + txRef}
          </p>
          <div className="mt-8 flex justify-center gap-3">
            <button onClick={() => navigate("/pay")} className="btn-green">Try Again</button>
            <Link to="/" className="btn-ghost">Back Home</Link>
          </div>
        </>
      )}
    </div>
  );
}
